import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const [authType, setAuthType] = useState('otp');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoOtp, setDemoOtp] = useState('');

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/otp/send', { email });
      setOtpSent(true);
      setDemoOtp(response.data.otp_demo);
      toast.success('OTP sent! Check below for demo OTP: ' + response.data.otp_demo);
    } catch (error) {
      toast.error('Failed to send OTP: ' + error.response?.data?.detail);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/otp/verify', { email, otp });
      localStorage.setItem('session_token', response.data.session_token);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Invalid OTP: ' + error.response?.data?.detail);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2">
            <span className="text-green-600">1DATA</span>
          </h1>
          <p className="text-gray-600 font-medium">by 1BUY.AI - AI-First Supply Chain Platform</p>
        </div>

        <Card className="bg-white shadow-xl border-gray-200">
          <CardHeader>
            <CardTitle className="text-gray-900 text-2xl">Welcome Back</CardTitle>
            <CardDescription className="text-gray-600">
              Choose your preferred login method
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={authType} onValueChange={setAuthType} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100">
                <TabsTrigger value="otp" data-testid="otp-tab" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">Email OTP</TabsTrigger>
                <TabsTrigger value="google" data-testid="google-tab" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">Google Auth</TabsTrigger>
              </TabsList>

              <TabsContent value="otp">
                {!otpSent ? (
                  <form onSubmit={handleSendOTP} className="space-y-4">
                    <div>
                      <Label htmlFor="email" className="text-gray-700">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="bg-white border-gray-300 text-gray-900"
                        data-testid="email-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      disabled={loading}
                      data-testid="send-otp-button"
                    >
                      {loading ? 'Sending...' : 'Send OTP'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOTP} className="space-y-4">
                    <div>
                      <Label htmlFor="otp" className="text-gray-700">Enter OTP</Label>
                      <Input
                        id="otp"
                        type="text"
                        placeholder="Enter 4-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        required
                        maxLength={4}
                        className="bg-white border-gray-300 text-gray-900 text-center text-2xl tracking-widest"
                        data-testid="otp-input"
                      />
                      {demoOtp && (
                        <p className="text-sm text-green-600 font-semibold mt-2 bg-green-50 p-2 rounded">
                          Demo OTP: {demoOtp}
                        </p>
                      )}
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      disabled={loading}
                      data-testid="verify-otp-button"
                    >
                      {loading ? 'Verifying...' : 'Verify & Login'}
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
                      onClick={() => { setOtpSent(false); setOtp(''); }}
                      data-testid="go-back-button"
                    >
                      Go Back
                    </Button>
                  </form>
                )}
              </TabsContent>

              <TabsContent value="google">
                <div className="space-y-4">
                  <p className="text-gray-600 text-sm text-center">
                    Login securely with your Google account
                  </p>
                  <Button
                    onClick={handleGoogleLogin}
                    className="w-full bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-300"
                    data-testid="google-login-button"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-gray-500 text-sm mt-6">
          By logging in, you agree to our Terms of Service and Privacy Policy
        </p>
        <p className="text-center text-gray-400 text-xs mt-2">
          Powered by <span className="font-semibold text-green-600">1BUY.AI</span>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;